// /admin/audit/subscriptions — manage scheduled audit-log report
// subscriptions.
//
// Phase 6 of the audit-log redesign delivers CRUD only: row state lives
// in DDB and is editable from this page; the runner Lambda + SES email
// pipeline is OUT OF SCOPE — see the deferred-banner at the top of the
// page. Once the runner ships, no UI changes are required here.
//
// Layout follows DESIGN.md §6 admin patterns and the v5 mockup
// `mockups/access-preview-v5.html`. Reuses <Table>, <StatusChip>, <Modal>,
// <Button>, and the shared toast API.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import {
  Button,
  Modal,
  StatusChip,
  Table,
  useToast,
  type TableColumn,
} from "@/components";
import {
  auditClient,
  type AuditSubscription,
  type AuditSubscriptionFilterSpec,
} from "@/data/clients/auditClient";
import { useRole } from "@/role";
import { useSession } from "@/session";
import "../AdminShared.css";
import "./Audit.css";
import { SubscribeModal } from "./SubscribeModal";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function describeFilterSpec(spec: AuditSubscriptionFilterSpec): string {
  const bits: string[] = [];
  if (spec.location_ids && spec.location_ids.length > 0) {
    bits.push(`locations: ${spec.location_ids.join(", ")}`);
  }
  if (spec.event_types && spec.event_types.length > 0) {
    bits.push(`events: ${spec.event_types.join(", ")}`);
  }
  if (spec.actor_aliases && spec.actor_aliases.length > 0) {
    bits.push(`actors: ${spec.actor_aliases.join(", ")}`);
  }
  if (spec.q && spec.q.length > 0) {
    bits.push(`q="${spec.q}"`);
  }
  return bits.length === 0 ? "all events" : bits.join(" · ");
}

function describeCadenceShort(cadence: string): string {
  switch (cadence) {
    case "DAILY":
      return "Daily";
    case "WEEKLY":
      return "Weekly";
    case "MONTHLY":
      return "Monthly";
    default:
      return cadence;
  }
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AdminAuditSubscriptionsPage(): JSX.Element {
  const handleApiError = useApiErrorHandler();
  const toast = useToast();
  const { scopedFetchToken } = useRole();
  const { session } = useSession();

  const [rows, setRows] = useState<AuditSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mutation flight — keyed by subscription id so the row's button can
  // disable while a PATCH/DELETE is in flight.
  const [pendingId, setPendingId] = useState<string | null>(null);

  // Edit modal target.
  const [editTarget, setEditTarget] = useState<AuditSubscription | null>(null);

  // Delete confirmation target.
  const [deleteTarget, setDeleteTarget] = useState<AuditSubscription | null>(
    null,
  );
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const defaultEmail = session?.alias ? `${session.alias}@amazon.com` : "";

  // ---------- load ----------
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await auditClient.listSubscriptions();
      setRows(resp.subscriptions ?? []);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.code ?? String(err.status));
        handleApiError(err, { actionLabel: "Load audit subscriptions" });
      } else {
        setError("unknown");
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    void reload();
  }, [reload, scopedFetchToken]);

  // ---------- pause/resume ----------
  async function togglePause(row: AuditSubscription) {
    const next = row.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    setPendingId(row.id);
    try {
      const updated = await auditClient.updateSubscription(row.id, {
        status: next,
      });
      setRows((cur) => cur.map((r) => (r.id === updated.id ? updated : r)));
      toast.success({
        title: next === "PAUSED" ? "Subscription paused" : "Subscription resumed",
      });
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, {
          actionLabel: next === "PAUSED" ? "Pause subscription" : "Resume subscription",
        });
      }
    } finally {
      setPendingId(null);
    }
  }

  // ---------- delete ----------
  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      await auditClient.deleteSubscription(deleteTarget.id);
      setRows((cur) => cur.filter((r) => r.id !== deleteTarget.id));
      toast.success({ title: "Subscription deleted" });
      setDeleteTarget(null);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Delete subscription" });
      }
    } finally {
      setDeleteSubmitting(false);
    }
  }

  // ---------- summary caption ----------
  const summary = useMemo(() => {
    const active = rows.filter((r) => r.status === "ACTIVE").length;
    const paused = rows.filter((r) => r.status === "PAUSED").length;
    const cad: Record<string, number> = {};
    for (const r of rows) cad[r.cadence] = (cad[r.cadence] ?? 0) + 1;
    const cadParts: string[] = [];
    for (const c of ["DAILY", "WEEKLY", "MONTHLY"] as const) {
      if (cad[c]) cadParts.push(`${cad[c]} ${describeCadenceShort(c).toLowerCase()}`);
    }
    return { active, paused, cadenceBreakdown: cadParts.join(", ") };
  }, [rows]);

  // ---------- columns ----------
  const columns: ReadonlyArray<TableColumn<AuditSubscription>> = useMemo(
    () => [
      {
        key: "cadence",
        header: "Cadence",
        width: "120px",
        render: (row) => (
          <span style={{ fontWeight: 500 }}>
            {describeCadenceShort(row.cadence)}
          </span>
        ),
      },
      {
        key: "filters",
        header: "Filters",
        render: (row) => (
          <span
            className="rb-audit-subs__filters-cell"
            title={describeFilterSpec(row.filter_spec)}
          >
            {describeFilterSpec(row.filter_spec)}
          </span>
        ),
      },
      {
        key: "email",
        header: "Email",
        width: "240px",
        render: (row) => (
          <code className="mono mono--sm">{row.email_to}</code>
        ),
      },
      {
        key: "next_run",
        header: "Next run",
        width: "180px",
        render: (row) =>
          row.status === "PAUSED" ? (
            <span className="admin-muted">paused</span>
          ) : (
            <code className="mono mono--sm">
              {formatTimestamp(row.next_run_at)}
            </code>
          ),
      },
      {
        key: "status",
        header: "Status",
        width: "120px",
        render: (row) =>
          row.status === "ACTIVE" ? (
            <StatusChip tone="success">Active</StatusChip>
          ) : (
            <StatusChip tone="warning">Paused</StatusChip>
          ),
      },
      {
        key: "actions",
        header: "",
        width: "240px",
        align: "right",
        render: (row) => {
          const busy = pendingId === row.id;
          return (
            <div className="rb-audit-subs__actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => togglePause(row)}
                disabled={busy}
              >
                {row.status === "ACTIVE" ? "Pause" : "Resume"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditTarget(row)}
                disabled={busy}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteTarget(row)}
                disabled={busy}
              >
                Delete
              </Button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingId],
  );

  // ---------- render ----------
  return (
    <section className="admin-page">
      <header className="rb-audit-subs__header">
        <Link to="/admin/audit" className="rb-audit-subs__back">
          ← Back to Audit log
        </Link>
        <div className="rb-audit-subs__title-row">
          <h1 className="page-title" style={{ margin: 0 }}>
            Audit subscriptions
          </h1>
        </div>
        <div className="rb-audit-subs__caption">
          {loading
            ? "Loading…"
            : rows.length === 0
              ? "No subscriptions yet."
              : `${summary.active} active · ${summary.paused} paused${
                  summary.cadenceBreakdown
                    ? ` · ${summary.cadenceBreakdown}`
                    : ""
                }`}
        </div>
      </header>

      {/* Phase-6 deferred-banner — runner Lambda + SES are NOT wired yet. */}
      <div className="rb-audit-subs__deferred-banner" role="status">
        Subscriptions are saved here. Email delivery is being scheduled —
        first runs land once the runner Lambda + SES sender are configured
        (deferred from Phase 6).
      </div>

      {error !== null && (
        <div className="card" style={{ marginBottom: 12 }}>
          <StatusChip tone="danger">
            <code className="mono mono--sm">{error}</code>
          </StatusChip>
          <span className="admin-muted"> Failed to load subscriptions.</span>
        </div>
      )}

      {!loading && rows.length === 0 && error === null ? (
        <div className="rb-audit-subs__empty">
          <div className="rb-audit-subs__empty-title">No subscriptions yet</div>
          <p style={{ margin: 0, fontSize: 13 }}>
            Open the{" "}
            <Link to="/admin/audit" style={{ color: "var(--primary-500)" }}>
              audit log
            </Link>
            , adjust filters to taste, then click <b>Subscribe</b> to receive
            scheduled CSV reports.
          </p>
        </div>
      ) : (
        <Table
          columns={columns}
          rows={rows}
          loading={loading}
          rowKey={(r) => r.id}
          emptyState="No subscriptions yet."
        />
      )}

      <SubscribeModal
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        onSaved={(updated) => {
          setRows((cur) =>
            cur.map((r) => (r.id === updated.id ? updated : r)),
          );
        }}
        defaultEmail={defaultEmail}
        existing={editTarget ?? undefined}
        mode="edit"
      />

      <Modal
        open={deleteTarget !== null}
        onClose={() => {
          if (!deleteSubmitting) setDeleteTarget(null);
        }}
        title="Delete subscription?"
        description={
          deleteTarget
            ? `${describeCadenceShort(deleteTarget.cadence)} report for ${deleteTarget.email_to}`
            : ""
        }
        variant="confirm"
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={confirmDelete}
              disabled={deleteSubmitting}
            >
              {deleteSubmitting ? "Deleting…" : "Delete"}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 13 }}>
          This subscription stops immediately. Past runs (if any) are kept.
        </p>
      </Modal>
    </section>
  );
}
